import type {
  CustomerJourney,
  DemandDriver,
  EmergingSignal,
  InteractionInsight,
  Intervention,
  MaterialChange,
  RootCauseHypothesis,
} from "./domain";
import type { AnalyticalContext } from "./analytical-context";

export type DemoProduct = AnalyticalContext["product"];

const productTerms: Record<Exclude<DemoProduct, "All">, string[]> = {
  "Current Account": [
    "payment",
    "direct-debit",
    "collection-date",
    "plan change",
  ],
  "Mobile App": ["android", "identity", "document-capture", "camera"],
  Delivery: ["delivery", "tracking", "order-status"],
  Insurance: ["insurance", "renewal", "price-breakdown"],
};

const matchesTerms = (value: string, terms: string[]) => {
  const normalised = value.toLowerCase();
  return terms.some((term) => normalised.includes(term));
};

export function matchesProduct(
  value:
    | Pick<InteractionInsight, "product">
    | Pick<DemandDriver, "product">
    | Pick<CustomerJourney, "product">,
  product: DemoProduct,
) {
  return product === "All" || value.product === product;
}

export function changeMatchesProduct(
  change: MaterialChange,
  product: DemoProduct,
) {
  if (product === "All") return true;
  return matchesTerms(
    [
      change.title,
      change.metric,
      change.impact,
      ...change.affectedCohorts,
    ].join(" "),
    productTerms[product],
  );
}

export function signalMatchesProduct(
  signal: EmergingSignal,
  product: DemoProduct,
) {
  if (product === "All") return true;
  return matchesTerms(
    [
      signal.title,
      signal.affectedPopulation,
      signal.impact,
      ...signal.likelyCauses,
    ].join(" "),
    productTerms[product],
  );
}

export function hypothesisMatchesProduct(
  hypothesis: RootCauseHypothesis,
  product: DemoProduct,
) {
  if (product === "All") return true;
  return matchesTerms(
    [
      hypothesis.title,
      hypothesis.symptom,
      hypothesis.cause,
      hypothesis.outcome,
      ...hypothesis.affectedCohorts,
    ].join(" "),
    productTerms[product],
  );
}

export function interventionMatchesProduct(
  intervention: Intervention,
  product: DemoProduct,
) {
  if (product === "All") return true;
  return matchesTerms(
    [
      intervention.title,
      intervention.targetCohort,
      intervention.successMetric,
    ].join(" "),
    productTerms[product],
  );
}

export type OverviewProfile = {
  eyebrow: string;
  title: string;
  description: string;
  decision: string;
  decisionDetail: string;
  focus: "android" | "direct-debit" | "delivery" | "renewal";
};

export const overviewProfiles: Record<DemoProduct, OverviewProfile> = {
  All: {
    eyebrow:
      "Executive brief · What materially changed and what decision needs attention?",
    title:
      "Three operational changes require attention; one intervention is working.",
    description:
      "Assisted contact is 12.4% above the prior period. Android verification, payment-date confusion and renewal explanation account for most observed growth; revised delivery tracking offsets part of it.",
    decision: "Protect onboarding capacity while validating two likely causes.",
    decisionDetail:
      "Approve the Android rollback measurement and assign Billing Operations to reconcile collection-date rules.",
    focus: "android",
  },
  "Mobile App": {
    eyebrow: "Mobile App brief · What is disrupting digital onboarding?",
    title:
      "Identity-verification abandonment increased 49.1% after version 8.14.",
    description:
      "The change is concentrated among Android 14 new customers. Document-capture failure language and assisted channel switches moved outside their expected ranges together.",
    decision: "Protect onboarding capacity while the rollback is measured.",
    decisionDetail:
      "Keep the Android rollback active, monitor manual-review guardrails and verify five further days of evidence before confirming the release as cause.",
    focus: "android",
  },
  "Current Account": {
    eyebrow:
      "Current Account brief · What is generating repeat service demand?",
    title:
      "Conflicting collection dates are driving repeat contact after plan changes.",
    description:
      "Payment-date confusion affects 5,840 contacts and carries a 31% repeat-contact rate. Portal and confirmation dates disagree most often for mid-cycle plan changes.",
    decision:
      "Reconcile collection-date rules across the portal and confirmation service.",
    decisionDetail:
      "Assign Billing Operations, verify the remaining contradictory cases and agree the repeat-contact baseline before launch.",
    focus: "direct-debit",
  },
  Delivery: {
    eyebrow: "Delivery brief · Did clearer tracking reduce avoidable demand?",
    title: "Revised tracking language reduced order-status contact by 13%.",
    description:
      "The improvement persists across two regions and remains outside the prior baseline. Failed-delivery complaints stayed within the agreed guardrail.",
    decision: "Keep the revised language and complete the measurement window.",
    decisionDetail:
      "Preserve the rollout, continue monitoring failed-delivery complaints and publish the intervention result after the observation window closes.",
    focus: "delivery",
  },
  Insurance: {
    eyebrow: "Insurance brief · Why is renewal service effort increasing?",
    title:
      "Renewal handling time increased 53.1% when price changes lacked explanation.",
    description:
      "Customers understand the total renewal price but not its components. Calls are longer, while campaign mix remains a credible alternative explanation.",
    decision:
      "Continue the price-breakdown test before changing the renewal estate.",
    decisionDetail:
      "Extend observation until the interval separates from no change and keep cancellation rate within its guardrail.",
    focus: "renewal",
  },
};

export function evidenceContext(origin: string) {
  const value = origin.toLowerCase();
  if (matchesTerms(value, productTerms["Mobile App"]))
    return { label: origin, drivers: ["Identity verification failure"] };
  if (matchesTerms(value, productTerms["Current Account"]))
    return { label: origin, drivers: ["Payment date confusion"] };
  if (matchesTerms(value, productTerms.Delivery))
    return { label: origin, drivers: ["Order status"] };
  if (matchesTerms(value, productTerms.Insurance))
    return { label: origin, drivers: ["Renewal price explanation"] };
  if (value.includes("contact volume") || value.includes("assisted contact"))
    return {
      label: origin,
      drivers: [
        "Identity verification failure",
        "Payment date confusion",
        "Order status",
        "Renewal price explanation",
      ],
    };
  return { label: origin, drivers: [] as string[] };
}

export function focusFromText(value: string | null | undefined) {
  const normalised = (value ?? "").toLowerCase();
  if (matchesTerms(normalised, productTerms["Current Account"]))
    return "direct-debit";
  if (matchesTerms(normalised, productTerms.Delivery)) return "delivery";
  if (matchesTerms(normalised, productTerms.Insurance)) return "renewal";
  return "android";
}

export function focusMatches(value: string, focus: string) {
  const product =
    focus === "direct-debit"
      ? "Current Account"
      : focus === "delivery"
        ? "Delivery"
        : focus === "renewal"
          ? "Insurance"
          : "Mobile App";
  return matchesTerms(value, productTerms[product]);
}
